import React, { useState } from "react";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import AdminAppBar from "../adminComponents/AdminAppBar";
import { showErrorToast, showSuccessToast } from "../Constant/Notification";
import { ToastContainer } from "react-toastify";
import ReportService from "../Services/ReportService";
import Loader from "../Constant/Loader";
import { getIcons } from "../Constant/Constant";
import { icons } from "lucide-react";
import { userUid } from "../../utils/storage";

const ReportMain = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportType, setReportType] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [leadReportType, setLeadReportType] = useState("");
  const [loading, setLoading] = useState(false);
  const Icon = getIcons();

  const tabs = ["Lead Report", "Deal Report"];

  const handleDownload = async () => {
    if (!startDate || !endDate || !reportType) {
      showErrorToast("Please select start date, end date, and report type");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      showErrorToast("Start date cannot be later than the end date.");
      return;
    }

    try {
      setLoading(true);
      const formatToDDMMYYYY = (date) => {
        const [yyyy, mm, dd] = date.split("-");
        return `${mm}-${dd}-${yyyy}`;
      };

      const formattedStartDate = formatToDDMMYYYY(startDate);
      const formattedEndDate = formatToDDMMYYYY(endDate);

      let response;
      if (reportType === "booking") {
        response = await ReportService.fetchBookingReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "agreement") {
        response = await ReportService.fegtchAgreementReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "siteVisit") {
        response = await ReportService.fetchSiteVisitReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "afterSiteVisit") {
        response = await ReportService.fetchAfterSitevisitReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "fullyRegistered") {
        response = await ReportService.fetchFullyRegisteredReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "sitting") {
        response = await ReportService.fetchSittingReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "afterSitting") {
        response = await ReportService.fetchAfterSittingReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "advanceAmount") {
        response = await ReportService.fetchAdvanceAmountReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "registrationProgress") {
        response = await ReportService.fetchRegistrationProgressReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "dealLostAfter") {
        response = await ReportService.fetchDealLostAfterReport(formattedStartDate, formattedEndDate);
      } else if (reportType === "dealLostBefore") {
        response = await ReportService.fetchDealLostBeforeReport(formattedStartDate, formattedEndDate);
      }

      const reportData = response.data;
      if (!Array.isArray(reportData) || reportData.length === 0) {
        showErrorToast("No data found for the selected criteria.");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Report");

      const headers = Object.keys(reportData[0]);
      worksheet.columns = headers.map((header) => ({
        header: header.trim(),
        key: header,
        width: 20,
      }));

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: reportType === "booking" ? "FF0070C0" : "FF000000" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: reportType === "booking" ? "F8CBAD" : "C4C6E4" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      reportData.forEach((item) => {
        worksheet.addRow(item);
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber !== 1) {
          row.height = 30;
          row.eachCell((cell) => {
            cell.alignment = { vertical: "middle", horizontal: "center" };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        } else {
          row.height = 60;
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `${reportType}_report_${startDate}_${endDate}.xlsx`);
    } catch (error) {
      console.error("Error downloading report:", error);
      showErrorToast("Failed to download report.");
    } finally {
      setLoading(false);
    }
  };

  const getLocalDate = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localDate = new Date(now.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split("T")[0];
  };

  const handleLeadReportDownload = async () => {
    if (!startDate || !endDate || !leadReportType) {
      showErrorToast("Please select start date, end date, and report type");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      showErrorToast("Start date cannot be later than the end date.");
      return;
    }

    try {
      setLoading(true);
      const formatToDDMMYYYY = (date) => {
        const [yyyy, mm, dd] = date.split("-");
        return `${mm}-${dd}-${yyyy}`;
      };

      const formattedStartDate = formatToDDMMYYYY(startDate);
      const formattedEndDate = formatToDDMMYYYY(endDate);

      let response;
      if (leadReportType === "leadReport") {
        const body = {
          userId : userUid,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        };
       ReportService.fetchLeadReport(body);
      }
      showSuccessToast("Report generation started. You'll receive an email shortly.");

       
    } catch (error) {
      console.error("Error downloading report:", error);
      showErrorToast("Failed to download report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {loading && <Loader />}
      <AdminAppBar />
      <ToastContainer />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Report Module</h1>
        </div>

        <div className="flex border-b border-gray-200 mb-8">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`px-8 py-4 text-xl font-bold border-b-2 transition-all duration-200 flex items-center gap-3 ${
                activeTab === index
                  ? "text-customGreen border-customGreen bg-green-50"
                  : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
              }`}
              onClick={() => {
                setActiveTab(index)
                setStartDate(null)
                setEndDate(null)
              }}
            >
              {/* Lead Report Icon */}
              {index === 0 && (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              )}
              {/* Deal Report Icon */}
              {index === 1 && (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              )}
              {tab}
            </button>
          ))}
        </div>

        <div className="bg-white shadow-lg grid gap-6 grid-cols-12 rounded-lg p-6">
          <div className="col-span-6">
<img   src={Icon?.reportBanner} className="rounded-xl"/>
          </div>

          <div className="col-span-6">
          {activeTab === 0 && (
            <div className="space-y-6">

            <h2 className="text-3xl font-bold text-gray-800"> Lead Report</h2>

              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-2">
                  Select Date Range
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition duration-200"
                      value={startDate}
                      max={getLocalDate()}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition duration-200"
                      value={endDate}
                      max={getLocalDate()}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-2">
                  Report Type
                </label>
                <select
                  value={leadReportType}
                  onChange={(e) => setLeadReportType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white transition duration-200"
                >
                  <option value="" disabled>
                    Select Report Type
                  </option>
                  <option value="leadReport">Lead Report</option>
                </select>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleLeadReportDownload}
                  className="flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  <span className="text-lg">Download Report</span>
                  <svg
                    className="ml-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className="space-y-6">

<h2 className="text-3xl font-bold text-gray-800"> Deal Report</h2>
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-2">
                  Select Date Range
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition duration-200"
                      value={startDate}
                      max={getLocalDate()}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition duration-200"
                      value={endDate}
                      max={getLocalDate()}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-2">
                  Report Type
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white transition duration-200"
                >
                  <option value="" disabled>
                    Select Report Type
                  </option>
                  <option value="siteVisit">Site Visit Report</option>
                  <option value="afterSiteVisit">After Site Visit Report</option>
                  <option value="sitting">Sitting Report</option>
                  <option value="afterSitting">After Sitting Report</option>
                  <option value="booking">Booking Report</option>
                  <option value="advanceAmount">Advance Amount Report</option>
                  <option value="registrationProgress">Registration In Progress Report</option>
                  <option value="fullyRegistered">Fully Registered</option>
                  <option value="dealLostBefore">Deal Lost Before Report</option>
                  <option value="dealLostAfter">Deal Lost After Report</option>
                </select>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  <span className="text-lg">Download Report</span>
                  <svg
                    className="ml-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportMain;